// CreateReportMenu — "Create new report" quick-launch dropdown, shared by the top-level
// Document Library page and the folder-tree DocumentBrowser (both standalone and inside a
// project's "Reports & documents" tab). Deep-links into the existing New Inspection /
// New Incident flows (which already own the template-picker + create logic), so every
// available published template ("Other report…") is reachable without duplicating them here.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FilePlus2, HardHat, ClipboardCheck, ClipboardList, Siren, MoreHorizontal, FileText } from "lucide-react";

const PINNED = [
    { code: "WEEKLY-HS", label: "Weekly H&S inspection", Icon: ClipboardCheck },
    { code: "QA-INSP", label: "QA inspection", Icon: ClipboardList },
];

export default function CreateReportMenu({ clientId, projectId, projectLabel, backTo, brand, allocations = [], onGenerate }) {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const accent = brand || "var(--brand-accent, #C75A1B)";

    const qs = (extra = {}) => {
        const p = new URLSearchParams({
            new: "1",
            ...(clientId ? { client: clientId } : {}),
            ...(projectId ? { project: projectId } : {}),
            ...(projectId && projectLabel ? { project_label: projectLabel } : {}),
            // "Back to project" only makes sense once we actually have a project to return to.
            ...(projectId && backTo ? { back: backTo } : {}),
            ...extra,
        });
        return p.toString();
    };
    const go = (path, extra) => { setOpen(false); navigate(`${path}?${qs(extra)}`); };
    // Site Induction has its own dedicated QR self-service workspace (Inductions module) —
    // it's not a NewInspectionDialog form like Weekly H&S/QA, so it gets its own route.
    const goInductions = () => { setOpen(false); navigate("/app/inductions"); };

    return (
        <div className="relative" data-testid="create-report-menu">
            <button data-testid="create-report-btn" onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold text-white"
                style={{ background: accent }}>
                <FilePlus2 size={15} /> Create new report <ChevronDown size={13} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-1.5 z-20 w-64 bg-white border rounded-xl shadow-lg overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                        {allocations.length > 0 && (
                            <div className="border-b" style={{ borderColor: "#F0EDE8" }}>
                                <div className="px-3.5 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F]">Allocated templates</div>
                                {allocations.map((a) => (
                                    <button key={a.id} data-testid={`create-report-allocation-${a.id}`}
                                        onClick={() => { setOpen(false); onGenerate?.(a); }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-[#1F1F1F] hover:bg-[#F6F4F0] text-left">
                                        <FileText size={14} style={{ color: accent }} />
                                        <span className="min-w-0">
                                            <span className="block truncate font-medium">{a.title || a.template_name}</span>
                                            <span className="block text-[11px] text-[#9A968F] truncate">{a.folder_name}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                        <button data-testid="create-report-SITE-INDUCTION" onClick={goInductions}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-[#1F1F1F] hover:bg-[#F6F4F0] text-left">
                            <HardHat size={14} style={{ color: accent }} /> Site induction
                        </button>
                        {PINNED.map((it) => (
                            <button key={it.code} data-testid={`create-report-${it.code}`}
                                onClick={() => go("/app/inspections", { template: it.code })}
                                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-[#1F1F1F] hover:bg-[#F6F4F0] text-left">
                                <it.Icon size={14} style={{ color: accent }} /> {it.label}
                            </button>
                        ))}
                        <button data-testid="create-report-incident" onClick={() => go("/app/incidents")}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-[#1F1F1F] hover:bg-[#F6F4F0] text-left border-t" style={{ borderColor: "#F0EDE8" }}>
                            <Siren size={14} className="text-red-600" /> Incident / near miss
                        </button>
                        <button data-testid="create-report-other" onClick={() => go("/app/inspections")}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-[#6F6A64] hover:bg-[#F6F4F0] text-left border-t" style={{ borderColor: "#F0EDE8" }}>
                            <MoreHorizontal size={14} /> Other report…
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
